import React, { useEffect, useId, useRef, useState } from "react";
import classNames from "classnames";
import { usePathname } from "next/navigation";

import { Action, Category, Label } from "@/constants/events";
import { MenuItem } from "@/custom-types/MenuItem";
import { MenuItemV2 } from "@/custom-types/MenuItemV2";
import { VariantWithDeliveryInfo } from "@/custom-types/Variant";
import { useATBStats } from "@/hooks/useATBStats";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useDispatch, useSelector } from "@/redux/hooks";
import { getCartSessionId } from "@/redux/selectors/cart";
import { getUserAgent } from "@/redux/selectors/userAgent";
import {
  disableLoadingSpinner,
  enableLoadingSpinner,
} from "@/redux/slices/header";

import { AddToBagModal } from "@/components/AddToBagModal";
import Button from "@/components/botanic/Button";
import { ButtonProps } from "@/components/botanic/Button/Button";

type AddToBagButtonProps = {
  addingToBagText?: string;
  className?: string;
  ctaText?: string;
  defaultVariantId: number;
  iconDimensions?: string;
  immediatelyAddToBag?: boolean;
  menuItem: MenuItem | MenuItemV2 | VariantWithDeliveryInfo;
  merchandisingCampaignId?: number;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onReservation?: () => void;
  quantity?: number;
  successText?: string;
  variant?: ButtonProps["variant"];
};

export const AddToBagButton: React.FC<AddToBagButtonProps> = ({
  addingToBagText = "adding",
  className,
  ctaText = "add to cart",
  defaultVariantId,
  iconDimensions,
  immediatelyAddToBag = false,
  menuItem,
  merchandisingCampaignId,
  onClick = () => {},
  onReservation = () => {},
  quantity,
  successText = "added!",
  variant,
}) => {
  const spinnerId = useId();
  const dispatch = useDispatch();
  const pathname = usePathname();
  const isBagPage = pathname === "/bag";
  const cartSessionId = useSelector(getCartSessionId);
  const [viewingModal, setViewingModal] = useState(false);
  const buttonRef = useRef(null);
  const { reportATBStats } = useATBStats();
  const { publishEvent } = useEventTracker();

  const [isAddingToBag, setIsAddingToBag] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { isMobile, isIOS } = useSelector(getUserAgent);
  useEffect(() => {
    if (showSuccessMessage) {
      setIsAddingToBag(false);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 1200);
    }
  }, [showSuccessMessage]);

  const dispatchBagSpinner = (
    action: typeof enableLoadingSpinner | typeof disableLoadingSpinner,
  ) => {
    if (isBagPage) {
      dispatch(action(spinnerId));
    }
  };

  const addToBag = async (e: React.MouseEvent<HTMLButtonElement>) => {
    dispatchBagSpinner(enableLoadingSpinner);
    setIsAddingToBag(true);
    onClick(e);

    publishEvent({
      action: Action.click,
      brandId: menuItem.brand?.id,
      cartId: cartSessionId,
      category: Category.addToBag,
      dispensaryId: menuItem.dispensaryId,
      label: menuItem.dispensary.inStoreCartEnabled
        ? `add to bag - shop every menu ${
            isMobile ? (isIOS ? "ios" : "android") : "desktop"
          }`
        : `${Label.addToBag}${
            isMobile ? (isIOS ? " ios" : " android") : " desktop"
          }`,
      menuItemId: menuItem.menuItemId,
      // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
      merchandisingCampaignId: merchandisingCampaignId,
      productId: menuItem.product?.id,
      strainId: menuItem.strain?.id,
    });
    setViewingModal(true);
  };

  // Once the AddToBagModal renders, it handles adding the menu item to bag in a useEffect
  return (
    <>
      <Button
        data-testid="add-to-cart-button"
        iconFilePath="cart_alt.svg"
        disabled={showSuccessMessage}
        onClick={addToBag}
        iconDimensions={iconDimensions}
        className={classNames("w-full disabled", className)}
        ref={buttonRef}
        variant={variant}
        submittingText={addingToBagText}
        isSubmitting={isAddingToBag}
      >
        {showSuccessMessage ? successText : ctaText}
      </Button>

      {viewingModal && (
        <AddToBagModal
          immediatelyAddToBag={immediatelyAddToBag}
          inStoreCart={menuItem.dispensary.inStoreCartEnabled}
          onDismiss={() => {
            setViewingModal(false);
            setIsAddingToBag(false);
            dispatchBagSpinner(disableLoadingSpinner);
          }}
          onReservation={() => {
            reportATBStats({ atbAction: true, atbError: undefined });
            setShowSuccessMessage(true);
            onReservation();
          }}
          onReservationFail={(error: string) => {
            setIsAddingToBag(false);
            dispatchBagSpinner(disableLoadingSpinner);
            reportATBStats({ atbAction: true, atbError: error });
          }}
          quantity={quantity}
          returnFocusRef={buttonRef}
          variantId={defaultVariantId}
        />
      )}
    </>
  );
};
